<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
// Painel de isolamento por departamento.
// Transferência sempre keep_thread (histórico único — cegueira desligada).

import { computed, onMounted, ref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import axios from 'axios';

const currentAccountId = useMapGetter('getCurrentAccountId');
const saving = ref(false);
const loading = ref(true);
const isolationOn = ref(false);
const transferMode = ref('keep_thread');

const settingsUrl = computed(
  () => `/api/v1/accounts/${currentAccountId.value}/department_settings`
);

const warningText = computed(() => {
  if (!isolationOn.value) {
    return 'Isolamento desligado: equipes organizam atendimento, mas não escondem conversas.';
  }
  return 'Isolamento ligado: agente só vê conversas do próprio departamento. Ao transferir, o histórico permanece na mesma conversa.';
});

const applyPayload = data => {
  isolationOn.value = Boolean(data?.department_isolation);
  transferMode.value = 'keep_thread';
};

const loadSettings = async () => {
  if (!currentAccountId.value) return;
  loading.value = true;
  try {
    const { data } = await axios.get(settingsUrl.value);
    applyPayload(data);
  } catch (e) {
    useAlert('Não foi possível carregar o isolamento.');
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  if (!currentAccountId.value) return;
  saving.value = true;
  try {
    const { data } = await axios.patch(settingsUrl.value, {
      department_isolation: isolationOn.value,
      department_transfer_mode: 'keep_thread',
      department_triage_name: 'atendimento',
    });
    applyPayload(data);
    useAlert('Configuração de departamentos salva.');
  } catch (e) {
    const msg =
      e?.response?.data?.error ||
      e?.response?.data?.errors?.[0] ||
      'Não foi possível salvar. Tente de novo.';
    useAlert(String(msg));
  } finally {
    saving.value = false;
  }
};

onMounted(loadSettings);
</script>

<template>
  <div
    class="mb-6 rounded-xl border border-n-weak bg-n-solid-2 p-4 flex flex-col gap-4"
  >
    <div>
      <h3 class="text-base font-medium text-n-slate-12">
        Isolamento por departamento
      </h3>
      <p class="text-sm text-n-slate-11 mt-1">
        {{ warningText }}
      </p>
    </div>

    <label
      class="flex items-center gap-3 text-sm text-n-slate-12 cursor-pointer"
    >
      <input
        v-model="isolationOn"
        type="checkbox"
        class="size-4 rounded border-n-weak"
        :disabled="loading || saving"
      />
      Ativar isolamento real entre departamentos
    </label>

    <div class="text-sm text-n-slate-11 max-w-md">
      <p class="font-medium text-n-slate-12">Histórico na transferência</p>
      <p class="mt-1">
        Sempre mantém a mesma conversa (histórico visível para o próximo
        atendente). A opção de “cegueira / conversa nova” está desabilitada.
      </p>
      <input v-model="transferMode" type="hidden" />
    </div>

    <div>
      <Button
        label="Salvar isolamento"
        size="sm"
        :is-loading="saving"
        :disabled="saving || loading"
        @click="save"
      />
    </div>
  </div>
</template>
